import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import './styles';
import RangeSlider from 'react-range-slider-input';
import 'react-range-slider-input/dist/style.css';
import { SliderContainer } from './styles';
import { useTheme } from '../../contexts/Theme/ThemeContext';
import debounce from "lodash/debounce";
import { currencyMask } from '../../services/Masks';
import { unmaskCurrency, unmaskToNumbers } from '../../services/Unmasks';

interface SliderProps {
    id?: string;
    className?: string;
    min?: number;
    max?: number;
    step?: number;
    value: [number, number];
    onThumbDragStart?: () => void;
    onThumbDragEnd?: () => void;
    onRangeDragStart?: () => void;
    onRangeDragEnd?: () => void;
    disabled?: boolean;
    rangeSlideDisabled?: boolean;
    thumbsDisabled?: [boolean, boolean];
    orientation?: 'horizontal' | 'vertical';
    debounceTime?: number;
    onDebounce?: (value: [number, number]) => void;
    onDebounceStart?: (value: [number, number]) => void;
    formatType?: 'currency' | 'integer';
}

const Slider: FC<SliderProps> = ({
    id = null,
    className = null,
    min = 0,
    max = 100,
    step = 1,
    value,
    onThumbDragStart,
    onThumbDragEnd,
    onRangeDragStart,
    onRangeDragEnd,
    disabled = false,
    rangeSlideDisabled = false,
    thumbsDisabled = [false, false],
    orientation = 'horizontal',
    debounceTime = 500,
    onDebounce,
    onDebounceStart,
    formatType = 'currency',
}) => {
    const { theme } = useTheme();

    const [localValue, setLocalValue] = useState<[number, number]>(value);
    const isDraggingRef = useRef(false);

    const onDebounceRef = useRef(onDebounce);
    const onDebounceStartRef = useRef(onDebounceStart);
    useEffect(() => {
        onDebounceRef.current = onDebounce;
        onDebounceStartRef.current = onDebounceStart;
    });

    const debouncedOnChangeRef = useRef(
        debounce((val: [number, number]) => {
            onDebounceRef.current?.(val);
            isDraggingRef.current = false;
        }, debounceTime)
    );
    const debouncedOnChange = debouncedOnChangeRef.current;

    useEffect(() => {
        return () => {
            debouncedOnChangeRef.current.cancel();
        };
    }, []);

    useEffect(() => {
        if (!isDraggingRef.current) {
            setLocalValue(value);
        }
    }, [value]);

    const handleChange = useCallback((newValue: [number, number]) => {
        isDraggingRef.current = true;
        setLocalValue(newValue);
        onDebounceStartRef.current?.(newValue);
        debouncedOnChange(newValue);
    }, [debouncedOnChange]);

    const handleMinInputChange = (e) => {
        if (formatType === 'integer') {
            const newVal = Math.min(parseInt(e.target.value) || 0, localValue[1]);
            handleChange([newVal, localValue[1]]);
            return;
        }
        let unmaskedValue = e.target.value.replace(/[^\d.]/g, "");
        unmaskedValue = currencyMask(unmaskedValue);
        unmaskedValue = unmaskCurrency(unmaskedValue);

        const newVal = Math.min(unmaskedValue, localValue[1]);
        handleChange([newVal, localValue[1]]);
    };

    const handleMaxInputChange = (e) => {
        if (formatType === 'integer') {
            let newVal = parseInt(e.target.value) || 0;
            newVal = Math.max(newVal, localValue[0]);
            if (newVal > max) return;
            handleChange([localValue[0], newVal]);
            return;
        }
        let unmaskedValue = e.target.value.replace(/[^\d.]/g, "");
        unmaskedValue = currencyMask(unmaskedValue);
        unmaskedValue = unmaskCurrency(unmaskedValue);

        const newVal = Math.max(unmaskedValue, localValue[0]);
        if(newVal > max) return;
        handleChange([localValue[0], newVal]);
    };

    return (
        <SliderContainer theme={theme}>
            <div className="input-slider-container min-value">
                <div className="input-wrapper">
                    <input
                        className='value'
                        type="text"
                        step={step}
                        value={formatType === 'integer'
                            ? Math.round(localValue[0] || 0).toString()
                            : currencyMask((localValue[0] || 0).toFixed(2), "R$")}
                        onChange={handleMinInputChange}
                        disabled={disabled}
                        min={min}
                        max={max}
                    />
                </div>
            </div>
            <RangeSlider
                id={id}
                className={className}
                min={min}
                max={max}
                step={step}
                value={[localValue[0], localValue[1]]}
                onInput={(values) => handleChange(values)}
                onThumbDragStart={onThumbDragStart}
                onThumbDragEnd={onThumbDragEnd}
                onRangeDragStart={onRangeDragStart}
                onRangeDragEnd={onRangeDragEnd}
                disabled={disabled}
                rangeSlideDisabled={rangeSlideDisabled}
                thumbsDisabled={thumbsDisabled}
                orientation={orientation}
            />
            <div className="input-slider-container max-value">
                <div className="input-wrapper">
                    <input
                        className='value'
                        type="text"
                        step={step}
                        value={formatType === 'integer'
                            ? Math.round(localValue[1] || 0).toString()
                            : currencyMask((localValue[1] || 0).toFixed(2), "R$")}
                        onChange={handleMaxInputChange}
                        disabled={disabled}
                        min={min}
                        max={max}
                    />
                </div>
            </div>
        </SliderContainer>
    );
};

export default Slider;
